/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react'
import tokens from '@lacedltd/design-tokens/tokens'
import { PremiumLabel } from '@lacedltd/laced-components'
import { useClickAway } from 'react-use'
import axios from 'axios'
import { useDebounce } from 'react-use'
import { useViewport, useLocale, useTranslation } from 'hooks'
import {
  withTranslations,
  cacheScrollPosition,
  getCachedScrollPosition,
  csrfToken,
  formatPrice
} from 'helpers'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { iconEllipsis } from 'images'
import Badge from '../atoms/Badge'
import PauseListing from '../admin/shared/PauseListing'
import {
  getPricingSuggestions,
  getQueuePlacement,
  DEBOUNCE_DELAY,
  PricingSuggestion
} from '../selling/common'
import InlinePriceAdjustPanel, {
  InlinePriceAdjustStyles
} from '../account/InlinePriceAdjust/InlinePriceAdjust'
import BottomSheet from '../account/InlinePriceAdjust/BottomSheet'
import PriceAdjustContent from '../account/InlinePriceAdjust/PriceAdjustContent'
import RetrievalModal from '../account/ConsignmentRetrieval/RetrievalModal'
import UpdateLabelModal from '../account/ConsignmentRetrieval/UpdateLabelModal'
import { PRICE_MATCHED_EVENT } from '../account/PriceMatchNotification'
import * as S from './ProductSaleItem.styles'
import { ProductSaleItemProps } from './ProductSaleItem.types'
import ProductSaleItemActions from './ProductSaleItemAction'
import ProductBarcodes from './ProductBarcodes'

const PRICE_ADJUST_OPEN_EVENT = 'inline-price-adjust:open'

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
)

const PencilIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
)

// Mobile wrapper that renders PriceAdjustContent inside a BottomSheet
interface MobilePriceAdjustProps {
  collectionId: number
  productId: number
  sizeConversionId: number
  sellerId: number
  currentPriceInCents: number
  currencyCode: string
  minimumPrice: number
  expanded: boolean
  handlingFee?: number
  paymentFee?: number
  shippingFee?: number
  onPriceChange?: (formattedPrice: string) => void
  onClose?: () => void
  onLastSoldFetched?: (formatted: string) => void
  productTitle: string
  productImageUrl: string
  currentPriceDisplay: string
}

const MobilePriceAdjust: React.FC<MobilePriceAdjustProps> = ({
  collectionId,
  productId,
  sizeConversionId,
  sellerId,
  currentPriceInCents,
  currencyCode,
  minimumPrice,
  expanded,
  handlingFee,
  paymentFee,
  shippingFee,
  onPriceChange,
  onClose,
  onLastSoldFetched,
  productTitle,
  productImageUrl,
  currentPriceDisplay
}) => {
  const { localiseUrl } = useLocale()
  const [suggestion, setSuggestion] = React.useState<PricingSuggestion | null>(
    null
  )
  const [lowestQueuePrice, setLowestQueuePrice] = React.useState<number | null>(
    null
  )
  const [queuePosition, setQueuePosition] = React.useState<number | null>(null)
  const [lowestPriceCurrency, setLowestPriceCurrency] =
    React.useState<string>(currencyCode)
  const [loading, setLoading] = React.useState(false)
  const [hasFetched, setHasFetched] = React.useState(false)
  const [saveState, setSaveState] = React.useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle')
  const [pendingPrice, setPendingPrice] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (hasFetched || !expanded) return

    const fetchData = async () => {
      setLoading(true)
      try {
        const [suggestionResult, queueResult] = await Promise.all([
          getPricingSuggestions({ productId, sizeConversionId, sellerId }),
          getQueuePlacement(
            {
              productId,
              priceInCents: currentPriceInCents,
              excludeId: collectionId,
              sizeConversionId,
              sellerId
            },
            () => {}
          )
        ])

        const effectiveSuggestion = suggestionResult ?? {
          price: currentPriceInCents / 100,
          label: 'last_sold' as const
        }

        setSuggestion(effectiveSuggestion)

        if (suggestionResult) {
          onLastSoldFetched?.(
            formatPrice(suggestionResult.price, { currencyCode })
          )
        }

        if (queueResult?.data?.lowest_price != null) {
          setLowestQueuePrice(queueResult.data.lowest_price)
        }

        if (queueResult?.data?.place_in_queue != null) {
          setQueuePosition(queueResult.data.place_in_queue)
        }

        if (queueResult?.data?.lowest_price_currency) {
          setLowestPriceCurrency(queueResult.data.lowest_price_currency)
        }

        setHasFetched(true)
      } catch {
        // Silently fail
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [
    expanded,
    hasFetched,
    productId,
    sizeConversionId,
    sellerId,
    currentPriceInCents,
    currencyCode,
    onLastSoldFetched
  ])

  React.useEffect(() => {
    if (expanded) {
      setPendingPrice(null)
      setSaveState('idle')
    }
  }, [expanded])

  // Re-fetch queue placement when the user adjusts the price via slider
  const updateQueuePlacement = React.useCallback(async () => {
    if (pendingPrice === null) return

    const priceInCents = Math.round(pendingPrice * 100)

    if (priceInCents === currentPriceInCents) return

    try {
      const queueResult = await getQueuePlacement(
        {
          productId,
          priceInCents,
          excludeId: collectionId,
          sizeConversionId,
          sellerId
        },
        () => {}
      )

      if (queueResult?.data?.place_in_queue != null) {
        setQueuePosition(queueResult.data.place_in_queue)
      }

      if (queueResult?.data?.lowest_price != null) {
        setLowestQueuePrice(queueResult.data.lowest_price)
      }

      if (queueResult?.data?.lowest_price_currency) {
        setLowestPriceCurrency(queueResult.data.lowest_price_currency)
      }
    } catch {
      // Silently fail
    }
  }, [pendingPrice, currentPriceInCents, productId, sizeConversionId, sellerId])

  useDebounce(updateQueuePlacement, DEBOUNCE_DELAY, [pendingPrice])

  const handleSave = React.useCallback(() => {
    if (pendingPrice === null || saveState === 'saving') return

    const priceInCents = Math.round(pendingPrice * 100)

    if (priceInCents === currentPriceInCents || priceInCents <= 0) return

    setSaveState('saving')
    axios
      .patch(
        localiseUrl(`/api/selling/${collectionId}/set_price`, {
          localeAsParam: true
        }),
        {
          price_in_cents: priceInCents,
          authenticity_token: csrfToken
        }
      )
      .then((response) => {
        setSaveState('saved')
        if (response.data?.price) {
          onPriceChange?.(response.data.price)
        }

        onClose?.()
      })
      .catch(() => {
        setSaveState('error')
      })
  }, [
    pendingPrice,
    saveState,
    currentPriceInCents,
    collectionId,
    localiseUrl,
    onPriceChange,
    onClose
  ])

  return (
    <BottomSheet
      open={expanded}
      onClose={() => onClose?.()}
      productTitle={productTitle}
      productImageUrl={productImageUrl}
      currentPrice={currentPriceDisplay}
    >
      <PriceAdjustContent
        suggestion={suggestion}
        minimumPrice={minimumPrice}
        lowestQueuePrice={lowestQueuePrice}
        currentPriceInCents={currentPriceInCents}
        currencyCode={currencyCode}
        selectedPrice={pendingPrice}
        onPriceSelect={(price) => setPendingPrice(price)}
        onMatchLastSold={() => {
          if (suggestion?.price) setPendingPrice(suggestion.price)
        }}
        onSave={handleSave}
        saveState={saveState}
        loading={loading}
        sellerId={sellerId}
        productId={productId}
        queuePosition={queuePosition}
        lowestPriceCurrency={lowestPriceCurrency}
        handlingFee={handlingFee}
        paymentFee={paymentFee}
        shippingFee={shippingFee}
      />
    </BottomSheet>
  )
}

const ProductSaleItem: React.FC<ProductSaleItemProps> = ({
  imageUrl,
  title,
  info,
  subInfo,
  price,
  actions,
  hasStatus = false,
  listingInfo = null,
  statusOverride,
  statusMessage,
  statusType,
  extraBadges = [],
  priceAction,
  showLowestPriceStatus = false,
  adminDeactivationReasons = [],
  lowestAlternativePrice,
  invalidSize = false,
  groupType = 'sizes',
  strikeSize = false,
  inlinePriceAdjust,
  priceBreakdown,
  retrievalActions
}) => {
  const { width } = useViewport()
  const { localiseUrl } = useLocale()
  const { t } = useTranslation()

  const actionsWrapRef = React.useRef(null)
  const [showActions, setShowActions] = React.useState(false)
  const [priceExpanded, setPriceExpanded] = React.useState(false)
  const [currentPrice, setCurrentPrice] = React.useState(price)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastSoldPrice, setLastSoldPrice] = React.useState<string | null>(null)
  const [showSaveSuccess, setShowSaveSuccess] = React.useState(false)
  const [priceMatchUpdating, setPriceMatchUpdating] = React.useState(false)
  const [priceMatched, setPriceMatched] = React.useState(false)
  const [deleteState, setDeleteState] = React.useState<
    'idle' | 'deleting' | 'deleted' | 'collapsing' | 'gone'
  >('idle')
  const hasRetrievalActions = Boolean(retrievalActions?.length)

  const handleDelete = React.useCallback(
    async (href: string) => {
      if (deleteState !== 'idle') return
      setDeleteState('deleting')
      try {
        await axios.delete(localiseUrl(href, { localeAsParam: true }), {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        })
        setDeleteState('deleted')
        setTimeout(() => setDeleteState('collapsing'), 1200)
        setTimeout(() => setDeleteState('gone'), 1600)
      } catch {
        setDeleteState('idle')
      }
    },
    [deleteState, localiseUrl]
  )

  const instanceId = React.useRef(
    `pa-${inlinePriceAdjust?.collectionId ?? Math.random()}`
  )

  // Listen for other sliders opening — close this one
  React.useEffect(() => {
    const handleOtherOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail

      if (detail !== instanceId.current) {
        setPriceExpanded(false)
      }
    }

    window.addEventListener(PRICE_ADJUST_OPEN_EVENT, handleOtherOpen)
    return () =>
      window.removeEventListener(PRICE_ADJUST_OPEN_EVENT, handleOtherOpen)
  }, [])

  const togglePriceExpanded = React.useCallback(() => {
    setPriceExpanded((prev) => {
      const next = !prev

      if (next) {
        // Broadcast so other instances close
        window.dispatchEvent(
          new CustomEvent(PRICE_ADJUST_OPEN_EVENT, {
            detail: instanceId.current
          })
        )
      }

      return next
    })
  }, [])

  const isMobile = width <= tokens.breakpoints.m
  const lowestPriceStatus = showLowestPriceStatus && lowestAlternativePrice

  const hasInvalidSizeOrNoLowestPrice =
    (showLowestPriceStatus && !lowestAlternativePrice) || invalidSize

  const hasInfo = !(
    hasInvalidSizeOrNoLowestPrice ||
    Boolean(lowestPriceStatus) ||
    hasInvalidSizeOrNoLowestPrice
  )

  useClickAway(actionsWrapRef, () => setShowActions(false))

  React.useEffect(() => {
    const cachedScrollPos = getCachedScrollPosition()

    if (!cachedScrollPos) return

    window.scrollTo(0, cachedScrollPos)
    // successful scrolling, reset cache
    if (window.scrollY === cachedScrollPos) {
      cacheScrollPosition(0)
    }
  }, [])

  const conversion = t(`shared.conversion.${groupType}`)

  if (deleteState === 'gone') return null

  const isDeleted = deleteState === 'deleted' || deleteState === 'collapsing'
  const overlayVisible = showSaveSuccess || isDeleted

  return (
    <S.ItemCollapse collapsing={deleteState === 'collapsing'}>
      <S.SaleItemWrap
        data-testid="product-sale-item"
        style={
          overlayVisible
            ? { borderColor: tokens.color.airForce[300] }
            : undefined
        }
      >
        {overlayVisible && (
          <S.SaveSuccessOverlay visible>
            <S.CheckCircle>
              <CheckIcon />
            </S.CheckCircle>
            {isDeleted && !priceMatched
              ? t('controllers.account.selling.removed_listing')
              : t('react_components.pricing_tasks.price_set')}
          </S.SaveSuccessOverlay>
        )}
        <img src={imageUrl} alt={title.label} />
        <S.SaleItemInnerWrap>
          <S.SaleItemInfoWrap>
            <S.SaleItemHeading
              as={title.href ? 'a' : 'h3'}
              href={localiseUrl(title.href)}
            >
              {title.label}
            </S.SaleItemHeading>

            <S.SaleItemSub>{info}</S.SaleItemSub>
            {(subInfo ||
              hasStatus ||
              statusOverride ||
              listingInfo?.premium ||
              extraBadges.length > 0) && (
              <S.SaleItemSubRow>
                {subInfo && (
                  <S.SaleItemSub type="accent">{subInfo}</S.SaleItemSub>
                )}
                {listingInfo?.premium && (
                  <S.PremiumLabelWrap>
                    <PremiumLabel value={listingInfo.premium} size="s" />
                  </S.PremiumLabelWrap>
                )}
                {(hasStatus || statusOverride) && (
                  <Badge
                    text={statusOverride || statusMessage}
                    type={statusType || 'default'}
                  />
                )}
                {extraBadges.map((badge) => (
                  <Badge
                    key={badge.text}
                    text={badge.text}
                    type={badge.type || 'default'}
                    tooltip={badge.tooltip}
                  />
                ))}
              </S.SaleItemSubRow>
            )}
          </S.SaleItemInfoWrap>
          <S.SaleItemPriceWrap center={hasInfo}>
            <S.SaleItemPriceRow>
              <S.SaleItemPrice
                strikethrough={!!adminDeactivationReasons.length || strikeSize}
              >
                {currentPrice}
              </S.SaleItemPrice>
              {inlinePriceAdjust && (
                <InlinePriceAdjustStyles.AdjustButton
                  onClick={togglePriceExpanded}
                  type="button"
                  aria-label="Adjust price"
                >
                  <PencilIcon />
                </InlinePriceAdjustStyles.AdjustButton>
              )}
            </S.SaleItemPriceRow>

            {priceBreakdown && (
              <S.SaleItemPriceBreakdown title={priceBreakdown}>
                {priceBreakdown}
              </S.SaleItemPriceBreakdown>
            )}

            {lowestPriceStatus && !priceMatched && (
              <S.SaleItemSub type="info">
                {t('react_components.selling.lowest')}: {lowestAlternativePrice}
              </S.SaleItemSub>
            )}

            {hasInvalidSizeOrNoLowestPrice && (
              <S.SaleItemSub type={invalidSize ? 'danger' : 'info'}>
                {t(
                  `react_components.selling.${
                    invalidSize ? 'conversion_invalid' : 'price_lowest'
                  }`,
                  { conversion }
                )}
              </S.SaleItemSub>
            )}

            {adminDeactivationReasons.map((reason) => (
              <S.SaleItemSub key={reason} type="danger">
                {reason}
              </S.SaleItemSub>
            ))}

            {priceAction && !priceMatched && (
              <S.PriceMatchButton
                href={localiseUrl(priceAction.href)}
                data-updating={priceMatchUpdating || undefined}
                onClick={async (e) => {
                  e.preventDefault()
                  if (!priceAction.id || priceMatchUpdating) return
                  setPriceMatchUpdating(true)
                  try {
                    const response = await axios.patch(
                      localiseUrl(`/api/selling/${priceAction.id}/price`, {
                        localeAsParam: true
                      }),
                      { authenticity_token: csrfToken }
                    )

                    if (response.data?.price) {
                      setCurrentPrice(response.data.price)
                    }

                    setPriceMatched(true)
                    setShowSaveSuccess(true)
                    window.dispatchEvent(new CustomEvent(PRICE_MATCHED_EVENT))
                    setTimeout(() => setDeleteState('collapsing'), 1200)
                    setTimeout(() => setDeleteState('gone'), 1600)
                  } catch {
                    // no-op
                  } finally {
                    setPriceMatchUpdating(false)
                  }
                }}
              >
                {priceAction.label}
              </S.PriceMatchButton>
            )}
          </S.SaleItemPriceWrap>

          {listingInfo && (listingInfo.info || listingInfo.warning) && (
            <S.SaleItemPriceWrap center={true}>
              {listingInfo.info && (
                <>
                  {listingInfo?.barcodes?.length === 1 && (
                    <S.SaleItemSub>{listingInfo.info}</S.SaleItemSub>
                  )}

                  {listingInfo?.barcodes?.length > 1 && (
                    <ProductBarcodes
                      barcodes={listingInfo.barcodes}
                      title={title}
                    />
                  )}
                </>
              )}
              {listingInfo.warning && (
                <p className="caption text-alert"> {listingInfo.warning} </p>
              )}
            </S.SaleItemPriceWrap>
          )}

          <S.SaleItemPriceActionsWrap
            ref={actionsWrapRef}
            showActions={showActions}
            hasRetrievalAction={hasRetrievalActions}
          >
            {/* Mobile: ellipsis to toggle actions dropdown */}
            {actions && actions.length > 0 && (
              <S.EllipsisButton
                type="button"
                aria-label="More actions"
                onClick={() => setShowActions((prev) => !prev)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <circle cx="8" cy="3" r="1.5" />
                  <circle cx="8" cy="8" r="1.5" />
                  <circle cx="8" cy="13" r="1.5" />
                </svg>
              </S.EllipsisButton>
            )}
            <div>
              {actions &&
                Boolean(actions.length) &&
                actions.map((action) => {
                  if (action.label === 'Pause' || action.label === 'Unpause') {
                    return (
                      <PauseListing
                        key={action.label}
                        pauseState={action.options.props.paused as boolean}
                        userId={action.options.props.userId as string}
                        id={action.options.props.id as string}
                      />
                    )
                  }

                  return (
                    <ProductSaleItemActions
                      key={action.label}
                      action={action}
                    />
                  )
                })}
            </div>
            {retrievalActions?.map((retrievalAction) =>
              retrievalAction.type === 'retrieve' ? (
                <RetrievalModal
                  key={`retrieve-${retrievalAction.saleCollectionId}`}
                  userId={retrievalAction.userId}
                  saleCollectionId={retrievalAction.saleCollectionId!}
                  productTitle={retrievalAction.productTitle}
                  productImageUrl={retrievalAction.productImageUrl}
                  size={retrievalAction.size}
                  styleCode={retrievalAction.styleCode}
                  buttonLabel={retrievalAction.buttonLabel}
                />
              ) : (
                <UpdateLabelModal
                  key={`update-${retrievalAction.retrievalId}`}
                  userId={retrievalAction.userId}
                  retrievalId={retrievalAction.retrievalId!}
                  productTitle={retrievalAction.productTitle}
                  productImageUrl={retrievalAction.productImageUrl}
                  size={retrievalAction.size}
                  styleCode={retrievalAction.styleCode}
                  showCancel={retrievalAction.showCancel}
                  cancelUrl={retrievalAction.cancelUrl}
                  cancelConfirm={retrievalAction.cancelConfirm}
                  cancelLabel={retrievalAction.cancelLabel}
                />
              )
            )}
          </S.SaleItemPriceActionsWrap>
        </S.SaleItemInnerWrap>
      </S.SaleItemWrap>
      {inlinePriceAdjust && !isMobile && (
        <InlinePriceAdjustPanel
          {...inlinePriceAdjust}
          expanded={priceExpanded}
          onPriceChange={(formatted) => {
            setCurrentPrice(formatted)
            setPriceExpanded(false)
            setShowSaveSuccess(true)
            setTimeout(() => setShowSaveSuccess(false), 1800)
          }}
          onClose={() => setPriceExpanded(false)}
          onLastSoldFetched={(formatted) => setLastSoldPrice(formatted)}
        />
      )}
      {inlinePriceAdjust && isMobile && (
        <MobilePriceAdjust
          {...inlinePriceAdjust}
          expanded={priceExpanded}
          onPriceChange={(formatted) => {
            setCurrentPrice(formatted)
            setPriceExpanded(false)
            setShowSaveSuccess(true)
            setTimeout(() => setShowSaveSuccess(false), 1800)
          }}
          onClose={() => setPriceExpanded(false)}
          onLastSoldFetched={(formatted) => setLastSoldPrice(formatted)}
          productTitle={title.label}
          productImageUrl={imageUrl}
          currentPriceDisplay={currentPrice || ''}
        />
      )}
    </S.ItemCollapse>
  )
}

export default withTranslations(ProductSaleItem)
